import { useMemo } from 'react'

import type { AssetName } from '../../hooks/useAssetNames'
import type { BlockView } from '../../hooks/useResolvedBlocks'
import type { PresentedSlide } from '../../hooks/usePresenter'
import type { EntityRef } from '../../types/presentation.types'
import { SlideRenderer } from '../SlideRenderer'
import { SlideStage } from '../SlideStage'
import { StoryDividerSlide } from './StoryDividerSlide'

/**
 * One presented slide at a given width: a real slide through SlideRenderer,
 * or a story divider (MYD-497). The presenter, its overview and the presenter
 * view all draw through this, so a divider looks the same in each.
 */
interface PresentedSlideViewProps {
	entry: PresentedSlide
	width: number
	blocks: Record<string, BlockView | undefined>
	theme?: string
	storyCount: number
	assetName: (ref: EntityRef | null) => AssetName | null
	className?: string
}

/**
 * The blocks a continuation slide draws (MYD-504): the same resolved content,
 * told which page to show. Renderers stay pure; the page arrives in the payload.
 */
const pagedBlocks = (entry: PresentedSlide, blocks: Record<string, BlockView | undefined>) => {
	if (!entry.continuation) return blocks
	const paged = { ...blocks }
	for (const block of entry.slide.blocks) {
		const view = blocks[block.id]
		if (view?.status === 'ok' && view.payload && typeof view.payload === 'object') {
			paged[block.id] = { ...view, payload: { ...(view.payload as object), page: entry.continuation.page } }
		}
	}
	return paged
}

export function PresentedSlideView({ entry, width, blocks, theme, storyCount, assetName, className }: PresentedSlideViewProps) {
	const name = entry.divider ? assetName(entry.story.anchor) : null
	const drawn = useMemo(() => pagedBlocks(entry, blocks), [entry, blocks])
	return (
		<SlideStage width={width} className={className}>
			{entry.divider ? (
				<StoryDividerSlide
					story={entry.story}
					storyNumber={entry.storyIndex + 1}
					storyCount={storyCount}
					asset={name && typeof name === 'object' ? name : null}
					theme={theme}
				/>
			) : (
				<SlideRenderer slide={entry.slide} blocks={drawn} theme={theme} />
			)}
		</SlideStage>
	)
}

export default PresentedSlideView
